// src/pages/EmailLog.jsx
// Console → Email Settings → Email Log (3.59.0, Super Admins): every email
// the portal sent (backend lib/mailer.js), newest first, kept 12 months.
// No email bodies: when, what kind, client, who it went to, the subject,
// which way it went (Amazon SES or Gmail) and its status from SES's
// delivery reports (delivered, bounced, marked as spam…).
// Also here: the switch (Amazon SES with the Gmail backup, or Gmail only),
// a test email to admin@ only, and the addresses that bounced or
// complained (not emailed again until cleared).
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotify } from '../components/Notifications';
import EmptyState from '../components/EmptyState';
import { API_BASE } from '../firebase/config';
import { timeZone, tzLabel } from '../utils/timeZone';
import { PILL, PILL_ON, PILL_ROW } from '../theme/tabs';
import Loader from '../components/Loader';
import ShowSelect from '../components/ShowSelect';
import Pager, { usePager } from '../components/Pager';
import Emo, { emoLabel } from '../components/Emo';

const KINDS = { incoming: 'Received fax', fax: 'Fax status', portal: 'Portal', gateway: 'Fax by email reply', alert: 'Alert', announcement: 'Announcement', test: 'Test', other: 'Other' };
// 3.65.4 (owner): two tabs: the Fax Portal's own emails and the support site's (support@, from SES's reports)
const SITES = [['portal', '📠 Fax Portal'], ['support', '🛟 Support site']];
const STATUS = {
  sent: ['Sent', 'var(--t-546e7a)'], delivered: ['Delivered', 'var(--t-2e7d32)'], delayed: ['Delayed', 'var(--t-e65100)'],
  bounced: ['Bounced', 'var(--t-b71c1c)'], complaint: ['Marked as spam', 'var(--t-b71c1c)'], rejected: ['Refused by SES', 'var(--t-b71c1c)'],
  failed: ['Not sent', 'var(--t-b71c1c)'], suppressed: ['Skipped (suppressed)', 'var(--t-78909c)'],
};
const DAYS = [1, 7, 30, 90, 365];

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { timeZone: timeZone(), month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' ' + tzLabel(new Date(iso));
}
// 3.59.1: a person's name above their address, when they have a portal account
const who = (r, people) => [...(r.to || []), ...(r.cc || [])].map(a => (
  <div key={a}>{people[a]?.name && <strong>{people[a].name} </strong>}<span style={{ color: 'var(--t-78909c)' }}>{a}</span></div>
)).concat(r.bccCount ? [<div key="bcc">+ {r.bccCount} Bcc</div>] : []);
// The client: the email's own, or (an email to one person) theirs
const clientOf = (r, names) => (r.accountId ? names[r.accountId] || r.accountId : (r.accountIds || []).map(id => names[id] || id).join(', ') || '—');

// compact (3.59.1): the Overview's Emails today pop-up: today's list only,
// without the switch, the test buttons and the suppressed addresses
export default function EmailLog({ compact = false }) {
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ days: compact ? 1 : 7, kind: '', status: '', q: '', site: 'portal' });
  const [qInput, setQInput] = useState('');
  const [suppressed, setSuppressed] = useState(null);
  // 3.60.5 (owner): Show and a pager over the loaded emails
  const [show, setShow] = useState('50');
  const { pageRows, pager } = usePager(rows, show);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`${API_BASE}/portal/email-log${path}`, { ...opts, headers: { Authorization: `Bearer ${await getToken()}`, 'Content-Type': 'application/json', ...(opts.headers || {}) } });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed');
    return json;
  }, [getToken]);

  const load = useCallback(async (before = null) => {
    setError('');
    try {
      const p = new URLSearchParams({ days: f.days, site: f.site, ...(f.kind && { kind: f.kind }), ...(f.status && { status: f.status }), ...(f.q && { q: f.q }), ...(before && { before }) });
      const j = await api(`?${p}`);
      setData(j);
      setRows(prev => (before ? [...prev, ...j.rows] : j.rows));
    } catch (e) { setError(e.message); }
  }, [api, f]);
  const loadSuppressed = useCallback(async () => {
    try { setSuppressed((await api('/suppressed')).rows); } catch (e) { setSuppressed([]); }
  }, [api]);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!compact) loadSuppressed(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function switchTo(provider) {
    if (provider === data.settings.provider) return;
    const ok = await confirm({
      title: provider === 'ses' ? 'Send through Amazon SES?' : 'Send through Gmail only?',
      message: provider === 'ses'
        ? 'Every email goes through Amazon SES. If SES refuses or can\'t be reached, Gmail sends it instead, as today.'
        : 'Every email goes through Gmail (admin@), as before 3.59.0. Gmail has a daily limit; SES delivery reports stop.',
      confirmLabel: provider === 'ses' ? 'Use Amazon SES' : 'Use Gmail only',
    });
    if (!ok) return;
    setBusy('switch');
    try {
      await api('/provider', { method: 'PUT', body: JSON.stringify({ provider }) });
      notify({ tone: 'success', title: provider === 'ses' ? 'Emails now go through Amazon SES' : 'Emails now go through Gmail only', detail: 'Every server picks it up within a minute.' });
      await load();
    } catch (e) { notify({ tone: 'error', title: "Couldn't switch", detail: e.message }); }
    setBusy('');
  }

  async function test(via) {
    setBusy(`test-${via}`);
    try {
      const r = await api('/test', { method: 'POST', body: JSON.stringify({ via }) });
      if (r.success) notify({ tone: 'success', title: `Test email sent to ${r.to}`, detail: `Through ${r.provider === 'ses' ? 'Amazon SES' : 'Gmail'}${r.fellBack ? ' (SES refused it: ' + r.fellBack + ')' : ''}` });
      else notify({ tone: 'error', title: "The test email wasn't sent", detail: r.error || (r.suppressed ? 'admin@ is on the suppressed list' : '') });
      await load();
    } catch (e) { notify({ tone: 'error', title: "The test email wasn't sent", detail: e.message }); }
    setBusy('');
  }

  // 3.59.3 (owner): the list as an Excel file, with the filters picked
  async function download() {
    setBusy('download');
    try {
      const p = new URLSearchParams({ days: f.days, site: f.site, ...(f.kind && { kind: f.kind }), ...(f.status && { status: f.status }), ...(f.q && { q: f.q }) });
      const res = await fetch(`${API_BASE}/portal/email-log/export?${p}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
      const url = URL.createObjectURL(await res.blob());
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/)?.[1] || 'Email_Log.xlsx';
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download the Email Log", detail: e.message }); }
    setBusy('');
  }

  async function clear(email) {
    const ok = await confirm({ title: `Email ${email} again?`, message: 'It bounced or was marked as spam. Clear it only once the address is fixed or the person asked for our emails.', confirmLabel: 'Clear' });
    if (!ok) return;
    try { await api(`/suppressed/${encodeURIComponent(email)}`, { method: 'DELETE' }); notify({ tone: 'success', title: `${email} cleared` }); loadSuppressed(); }
    catch (e) { notify({ tone: 'error', title: "Couldn't clear it", detail: e.message }); }
  }

  if (error && !data) return <div style={S.errorBanner}>{error}</div>;
  if (!data) return <Loader />;
  const st = data.settings;
  const names = data.names || {};
  const people = data.people || {};

  return (
    <div>
      {!compact && <>
      <h3 style={S.title}><Emo e="📬" /> Email Log</h3>
      <p style={S.sub}>
        Every email the portal sends, newest first, kept for 12 months. Only who, when and the subject are kept, never the email itself or its attachments.
        With Amazon SES, each email's status comes from SES (delivered, bounced, marked as spam). An address that bounces for good or marks us as spam
        isn't emailed again until you clear it below.
      </p>

      <div style={S.card}>
        <div style={S.row}>
          <div>
            <div style={S.cardTitle}>Emails go through</div>
            <div style={PILL_ROW}>
              <button style={{ ...PILL, ...(st.provider === 'ses' ? PILL_ON : {}) }} disabled={!!busy || !st.sesReady} onClick={() => switchTo('ses')} title={st.sesReady ? '' : "Amazon SES isn't set up yet"}>Amazon SES (Gmail backup)</button>
              <button style={{ ...PILL, ...(st.provider === 'gmail' ? PILL_ON : {}) }} disabled={!!busy} onClick={() => switchTo('gmail')}>Gmail only</button>
            </div>
            <div style={{ ...S.muted, marginTop: 8 }}>
              {st.sesReady ? <>Amazon SES is set up ({st.region}).</> : <>Amazon SES isn't set up yet, so Gmail sends everything.</>}
              {' '}Delivery reports from SES: {st.eventsReady ? <span style={S.on}>connected</span> : <span style={S.off}>not connected yet</span>}.
              {st.changedBy && <> Switched by {st.changedBy} {fmt(st.changedAt)}.</>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.ghostBtn} disabled={!!busy || !st.sesReady} onClick={() => test('ses')}>{busy === 'test-ses' ? 'Sending…' : <><Emo e="✉️" /> Test Amazon SES</>}</button>
            <button style={S.ghostBtn} disabled={!!busy} onClick={() => test('switch')}>{busy === 'test-switch' ? 'Sending…' : <><Emo e="✉️" /> Test current way</>}</button>
          </div>
        </div>
        <div style={S.small}>Test emails go to admin@crazypixels.org only.</div>
      </div>

      </>}
      {/* 3.60.4 (owner): how many emails were sent and received, by day / week / month */}
      {!compact && <EmailCounts />}

      <div style={{ ...PILL_ROW, margin: '4px 0 10px' }} role="tablist" aria-label="Which emails">
        {SITES.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={f.site === k} onClick={() => setF({ ...f, site: k, kind: '' })} style={{ ...PILL, ...(f.site === k ? PILL_ON : {}) }}>{emoLabel(label)}</button>
        ))}
      </div>
      {f.site === 'support' && <div style={{ ...S.small, marginBottom: 8 }}>Emails from support.crazypixels.org (support@), sent through Amazon SES since October 23, 2026, from SES's delivery reports: who, when, the subject and what happened to it.</div>}
      <div style={S.filters}>
        <select style={S.select} value={f.days} onChange={e => setF({ ...f, days: Number(e.target.value) })}>
          {DAYS.map(d => <option key={d} value={d}>{d === 1 ? 'Last 24 hours' : `Last ${d} days`}</option>)}
        </select>
        {f.site === 'portal' && <select style={S.select} value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })}>
          <option value="">All kinds</option>
          {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>}
        <select style={S.select} value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
          <option value="">Any status</option>
          {Object.entries(STATUS).map(([k, [v]]) => <option key={k} value={k}>{v}</option>)}
          <option value="fellBack">Sent by the Gmail backup</option>
        </select>
        <form onSubmit={e => { e.preventDefault(); setF({ ...f, q: qInput.trim() }); }} style={{ display: 'flex', gap: 6 }}>
          <input style={S.input} placeholder="Address or subject" value={qInput} onChange={e => setQInput(e.target.value)} />
          <button type="submit" style={S.ghostBtn}>Search</button>
        </form>
        <ShowSelect value={show} onChange={setShow} style={S.select} groupStyle={{ display: 'flex', alignItems: 'center', gap: 6 }} labelStyle={{ fontSize: 12, color: 'var(--t-546e7a)', fontWeight: 600 }} />
        <button style={S.ghostBtn} onClick={() => load()}><Emo e="↻" /> Refresh</button>
        <button style={S.ghostBtn} onClick={download} disabled={busy === 'download'} title="The emails matching these filters, as an Excel file (up to 20,000)">{busy === 'download' ? 'Preparing…' : <><Emo e="📥" /> Download (Excel)</>}</button>
      </div>

      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={S.th}>When</th><th style={S.th}>Kind</th><th style={S.th}>Client</th><th style={S.th}>To</th><th style={S.th}>Subject</th><th style={S.th}>Sent by</th><th style={S.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} style={S.emptyCell}><EmptyState icon={<Emo e="📬" />} emptyText="No emails match." /></td></tr>
            ) : pageRows.map((r, i) => {
              const [label, color] = STATUS[r.status] || [r.status, 'var(--t-546e7a)'];
              const isOpen = open === r.id;
              return [
                <tr key={r.id} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)', cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : r.id)}>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(r.at)}</td>
                  <td style={S.td}>{r.kind === 'support' ? 'Support site' : (KINDS[r.kind] || r.kind || '—')}</td>
                  <td style={S.td}>{clientOf(r, names)}</td>
                  <td style={{ ...S.td, maxWidth: 280, wordBreak: 'break-word', fontSize: 12 }}>{who(r, people)}</td>
                  <td style={{ ...S.td, maxWidth: 320 }}>{r.subject}{r.tooLarge && <div style={S.small}>Fax too large to attach{r.resent ? ' (sent again without it)' : ''}</div>}</td>
                  <td style={S.td}>{r.provider === 'ses' ? 'Amazon SES' : r.provider === 'gmail' ? (r.fellBack ? <>Gmail <span style={S.backup}>backup</span></> : 'Gmail') : '—'}
                    {/* 3.62.29: sent from a computer, not the live server (our tests); Health leaves those to @crazypixels.org out */}
                    {r.local && <> <span style={S.backup} title="Sent from a computer, not the live server (a test). Health doesn't count these when they only go to @crazypixels.org addresses.">test, not live server</span></>}</td>
                  <td style={{ ...S.td, color, fontWeight: 700, whiteSpace: 'nowrap' }}>{label}</td>
                </tr>,
                isOpen && (
                  <tr key={r.id + '-d'} style={S.tr}><td colSpan={7} style={S.detail}>
                    {r.fellBack && <div><strong>Why Gmail sent it:</strong> SES said {r.fellBack}</div>}
                    {r.error && <div><strong>Error:</strong> {r.error}</div>}
                    {r.suppressed?.length > 0 && <div><strong>Skipped (suppressed):</strong> {r.suppressed.join(', ')}</div>}
                    {r.faxId && <div><strong>Fax:</strong> {r.faxId}</div>}
                    {(r.events || []).length > 0 ? (r.events || []).map((e, k) => (
                      <div key={k}>{fmt(e.at)}: <strong>{(STATUS[e.type] || [e.type])[0]}</strong>
                        {e.recipients?.length > 0 && <> · {e.recipients.map(x => x.email + (x.code ? ` (${x.code})` : '')).join(', ')}</>}
                        {e.bounceType && <> · {e.bounceType}{e.bounceSubType ? ` / ${e.bounceSubType}` : ''}</>}
                        {e.resentWithoutFile && <> · sent again without the fax</>}
                        {e.reason && <> · {e.reason}</>}
                      </div>
                    )) : <div style={S.small}>{r.provider === 'ses' ? 'No delivery report yet.' : 'Gmail sends no delivery reports.'}</div>}
                  </td></tr>
                ),
              ];
            })}
          </tbody>
        </table>
      </div>
      <Pager {...pager} label="emails" />
      {data.next && <div style={{ textAlign: 'center', marginTop: 8 }}><button style={S.ghostBtn} onClick={() => load(data.next)}>Load older emails</button></div>}

      {!compact && <>
      <h4 style={S.h4}>Not emailed (bounced or marked as spam)</h4>
      <p style={S.muted}>The portal skips these addresses. Clear one once the address is fixed or the person asks for our emails again.</p>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr style={S.thead}><th style={S.th}>Address</th><th style={S.th}>Why</th><th style={S.th}>Since</th><th style={S.th}></th></tr></thead>
          <tbody>
            {!suppressed ? <tr><td colSpan={4} style={S.emptyCell}><Loader /></td></tr>
              : suppressed.length === 0 ? <tr><td colSpan={4} style={S.emptyCell}><EmptyState icon={<Emo e="✅" />} emptyText="No addresses are being skipped." /></td></tr>
              : suppressed.map((s, i) => (
                <tr key={s.email} style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)' }}>
                  <td style={S.td}>{s.email}</td>
                  <td style={S.td}>{s.reason === 'complaint' ? 'Marked as spam' : 'Bounced'}{s.code && <div style={S.small}>{s.code}</div>}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap', fontSize: 12 }}>{fmt(s.at)}</td>
                  <td style={{ ...S.td, textAlign: 'right' }}><button style={S.ghostBtn} onClick={() => clear(s.email)}>Clear</button></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      </>}
    </div>
  );
}

const S = {
  countCards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 8, margin: '12px 0 10px' },
  countCard: { background: 'var(--c-f7faf9)', border: '1px solid var(--c-e0f2f1)', borderRadius: 10, padding: '8px 10px', textAlign: 'center' },
  countN: { fontSize: 20, fontWeight: 700, color: 'var(--t-00695c)' },
  countL: { fontSize: 11.5, color: 'var(--t-78909c)' },
  title    : { margin: '0 0 6px', color: 'var(--t-00695c)', fontSize: 18, fontWeight: 700 },
  sub      : { margin: '0 0 16px', color: 'var(--t-546e7a)', fontSize: 13, lineHeight: 1.6, maxWidth: 980 },
  h4       : { margin: '22px 0 4px', color: 'var(--t-00695c)', fontSize: 15 },
  muted    : { color: 'var(--t-78909c)', fontSize: 13, margin: '0 0 10px' },
  small    : { fontSize: 11, color: 'var(--t-78909c)', marginTop: 6 },
  card     : { background: 'var(--c-ffffff)', border: '1px solid var(--c-e0f2f1)', borderRadius: 12, padding: '16px 20px', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', marginBottom: 16 },
  row      : { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--t-37474f)', marginBottom: 8 },
  on       : { color: 'var(--t-2e7d32)', fontWeight: 700 },
  off      : { color: 'var(--t-e65100)', fontWeight: 700 },
  backup   : { fontSize: 11, fontWeight: 700, color: 'var(--t-e65100)', background: 'var(--c-fff3e0)', borderRadius: 8, padding: '1px 6px' },
  filters  : { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 },
  select   : { padding: '7px 10px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  input    : { padding: '7px 10px', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 200, background: 'var(--c-ffffff)', color: 'var(--t-37474f)' },
  ghostBtn : { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-546e7a)', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  errorBanner: { background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  tableWrap: { background: 'var(--c-ffffff)', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)' },
  table    : { width: '100%', borderCollapse: 'collapse' },
  thead    : { background: 'var(--c-00695c)' },
  th       : { padding: '12px 14px', textAlign: 'left', color: 'var(--t-ffffff)', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr       : { borderBottom: '1px solid var(--c-f0f0f0)' },
  td       : { padding: '10px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'top' },
  detail   : { padding: '10px 18px 14px', fontSize: 12, color: 'var(--t-37474f)', background: 'var(--c-f0faf8)', lineHeight: 1.8 },
  emptyCell: { textAlign: 'center', padding: '40px 0' },
};

// ── Email counts (3.60.4, owner) ─────────────────────────────
// Emails sent (Amazon SES / Gmail, delivered, bounced, by kind) and received
// by fax by email (fax@, privatefax@, number addresses), by day, week or
// month, from one small record a day (backend lib/emailCounts.js).
const COUNT_PERIODS = [[1, 'Today'], [7, '7 days'], [30, '30 days'], [90, '90 days'], [365, '12 months']];
function EmailCounts() {
  const { getToken } = useAuth();
  const { notify } = useNotify();
  const [days, setDays] = useState(7);
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let gone = false;
    setC(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/portal/email-log/counts?days=${days}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
        const j = await r.json(); if (!r.ok) throw new Error(j.error);
        if (!gone) setC(j);
      } catch (e) { if (!gone) setC({ error: e.message }); }
    })();
    return () => { gone = true; };
  }, [days, getToken]);
  async function download() {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/portal/email-log/counts/export?days=${days}`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Download failed');
      const url = URL.createObjectURL(await res.blob());
      const name = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/)?.[1] || 'Email_counts.xlsx';
      const a = Object.assign(document.createElement('a'), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { notify({ tone: 'error', title: "Couldn't download the email counts", detail: e.message }); }
    setBusy(false);
  }
  const n = v => Number(v || 0).toLocaleString('en-US');
  const label = p => (c.group === 'month' ? new Date(`${p}-15T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
    : `${c.group === 'week' ? 'Week of ' : ''}${new Date(`${p}T12:00:00Z`).toLocaleDateString('en-US', { weekday: c.group === 'day' ? 'short' : undefined, month: 'short', day: 'numeric', timeZone: 'UTC' })}`);
  const t = c?.total;
  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={S.row}>
        <div style={S.cardTitle}><Emo e="📊" /> Email counts <span style={{ ...S.small, fontWeight: 400 }}>(US Eastern days)</span></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={PILL_ROW}>{COUNT_PERIODS.map(([d, l]) => <button key={d} onClick={() => setDays(d)} style={{ ...PILL, ...(days === d ? PILL_ON : {}), fontSize: 12 }} aria-pressed={days === d}>{l}</button>)}</div>
          <button style={S.ghostBtn} onClick={download} disabled={busy || !c?.rows}>{busy ? 'Preparing…' : <><Emo e="📥" /> Download (Excel)</>}</button>
        </div>
      </div>
      {!c ? <Loader /> : c.error ? <div style={S.errorBanner}>{c.error}</div> : (
        <>
          <div style={S.countCards}>
            {[['Sent', t.sent], ['Amazon SES', t.sentSes], ['Gmail', t.sentGmail], ['Delivered', t.delivered], ['Bounced', t.bounced], ['Marked as spam', t.complaint], ['Not sent', t.notSent], ['Received', t.received]].map(([l, v]) => (
              <div key={l} style={S.countCard}><div style={S.countN}>{n(v)}</div><div style={S.countL}>{l}</div></div>))}
          </div>
          <div style={{ ...S.small, margin: '0 0 8px' }}>
            Sent by kind: fax status {n(t.kFax)} · received-fax emails {n(t.kIncoming)} · portal {n(t.kPortal)} · fax by email replies {n(t.kGateway)} · alerts {n(t.kAlert)} · announcements {n(t.kAnnouncement)} · tests and other {n(t.kOther)}.
            {' '}Received (fax by email): fax@ {n(t.recvFax)} · privatefax@ {n(t.recvPrivate)} · number addresses {n(t.recvNumber)}.
          </div>
          {c.rows.length > 1 && (
            <div style={{ ...S.tableWrap, maxHeight: 320 }}>
              <table style={S.table}>
                <thead><tr style={S.thead}>{['', 'Sent', 'Amazon SES', 'Gmail', 'Delivered', 'Bounced', 'Spam', 'Not sent', 'Received', 'fax@', 'privatefax@', 'Number addresses'].map(h => <th key={h} style={{ ...S.th, textAlign: h ? 'right' : 'left' }}>{h}</th>)}</tr></thead>
                <tbody>
                  {c.rows.map((r, i) => (
                    <tr key={r.period} style={{ ...S.tr, background: i % 2 ? 'var(--c-ffffff)' : 'var(--c-fafafa)' }}>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{label(r.period)}</td>
                      {['sent', 'sentSes', 'sentGmail', 'delivered', 'bounced', 'complaint', 'notSent', 'received', 'recvFax', 'recvPrivate', 'recvNumber'].map(k => (
                        <td key={k} style={{ ...S.td, textAlign: 'right', fontWeight: k === 'sent' || k === 'received' ? 700 : 400, color: (k === 'bounced' || k === 'complaint' || k === 'notSent') && r[k] ? 'var(--t-b71c1c)' : undefined }}>{n(r[k])}</td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={S.small}>Counted from Oct 14, 2026 (when emails moved to the Email Log). Received = emails to fax@, privatefax@ and number addresses that fax by email handled.</div>
        </>
      )}
    </div>
  );
}

